/* eslint-disable css-modules/no-unused-class, no-extra-boolean-cast */
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Xarrow from 'react-xarrows';
import { useNavigate } from 'react-router-dom';
import { translate } from 'translations';
import { coreService } from 'network-utils';
import { Avatar } from 'learning-panel-components';
import { languagesNameToDisplayName } from 'utils';
import ProgramLanguageSelectionContainer from '@components/common/programLanguageSelection/programLanguageSelectionContainer';
import { LearningPathNodeProps } from '@components/learningPath/learningPathInterfaces';
import ContentItem, { ContentItemStatus } from '@components/learningPath/contentItems/contentItem';
import Inventory from '@components/learningPath/Inventory/inventory';
import CertificatePopupContainer from '@components/home/certificates/certificatePopupContainer';
import { useSelector } from 'react-redux';
import { selectUserNickname, selectUserFullName } from '@redux/slices/userSlice';
import SkillTreeOverview from '../overview/overview';
import SkillTreePathButton from '../skillTreePathButton/skillTreePathButton';
import styles from './skillTreeNavigation.module.scss';
import LearningPathOverviewContainer from '../learningPathOverview/learningPathOverviewContainer';
import MapLegend from '../mapLegend/mapLegend';

const SkillTreeNavigation: FC<SkillTreeNavigationProps> = (props: SkillTreeNavigationProps) => {
    const { name, uuid, learningPaths, overallData, onSelectLearningPath, selectedLearningPathUuid, learningPathData } =
        props;
    const [activeLearningPathUuid, setActiveLearningPathUuid] = useState<string>(
        selectedLearningPathUuid || learningPaths[0]?.uuid
    );
    const [displayedLearningPathUuid, setDisplayedLearningPathUuid] = useState<string>();
    const [learningPathWithLocations, setLearningPathWithLocations] = useState<any>(null);
    const [viewedLearningPathUuid, setViewedLearningPathUuid] = useState<string | null>(null);
    const [nodeIdsToNodesMap, setNodeIdsToNodesMap] = useState<{ [key: number]: LearningPathNodeProps }>({});
    const [levelToNodeIdsMap, setLevelToNodeIdsMap] = useState<{ [key: number]: LearningPathNodeProps[] }>({});
    const [areNodesRendered, setAreNodesRendered] = useState<boolean>(false);
    const [areNavigationButtonRendered, setAreNavigationButtonRendered] = useState<boolean>(false);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [hoveredType, setHoveredType] = useState<string | null>(null);
    const [hoveredElements, setHoveredElements] = useState<{ type: string; id: string }[]>([]);
    const [currentLevel, setCurrentLevel] = useState<number>(-1);
    const [showLpCertificate, setShowLpCertificate] = useState<boolean>(false);
    const [showSkillTreeCertificate, setShowSkillTreeCertificate] = useState<boolean>(false);
    const [scrollPercent, setScrollPercent] = useState<number>(0);
    const [inventoryId, setInventoryId] = useState<string>('');
    const [showProgramLanguageSelection, setShowProgramLanguageSelection] = useState<boolean>(false);
    const [bottomRendered, setBottomRendered] = useState<boolean>(false);
    const nickname = useSelector(selectUserNickname);
    const fullName = useSelector(selectUserFullName);

    const inventoryRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const containerRef = useRef<any>(null);
    const currentLevelRef = useRef<any>(null);
    const navigate = useNavigate();
    const showUserAvatarInRoot =
        props.learningPathData.status === 'not_started' || props.learningPathData.status === null;

    useEffect(() => {
        let currLevel = 0;
        const levels = Object.keys(levelToNodeIdsMap).map(level => parseInt(level, 10));
        if (levels.length === 0) return;
        const maxLevel = Math.max(...levels);

        for (let level = 0; level <= maxLevel; level++) {
            const nodes = levelToNodeIdsMap[level];
            if (nodes.every(node => node.status === null)) {
                break;
            }
            currLevel = level;
        }

        setCurrentLevel(currLevel);
    }, [levelToNodeIdsMap]);

    useEffect(() => {
        if (overallData.languages.length === 0) setShowProgramLanguageSelection(true);
    }, [overallData.languages]);

    useEffect(() => {
        const nodeIdsToNodesMapTmp: { [key: number]: LearningPathNodeProps } = {};
        learningPathData.nodes.forEach((node: any) => {
            nodeIdsToNodesMapTmp[node.id] = node;
        });
        setNodeIdsToNodesMap(nodeIdsToNodesMapTmp);

        const levelToNodeIdsTmpMap: { [key: number]: LearningPathNodeProps[] } = {};
        const nodeIdsToLevelMap: { [key: number]: number } = {};

        const calcLevel = (node: LearningPathNodeProps, level: number) => {
            if (nodeIdsToLevelMap[node.id] !== undefined) {
                const prevLevel = nodeIdsToLevelMap[node.id];
                if (prevLevel >= level) {
                    return;
                }

                levelToNodeIdsTmpMap[prevLevel] = levelToNodeIdsTmpMap[prevLevel].filter(n => n.id !== node.id);
            }

            nodeIdsToLevelMap[node.id] = level;
            if (levelToNodeIdsTmpMap[level] === undefined) {
                levelToNodeIdsTmpMap[level] = [];
            }
            levelToNodeIdsTmpMap[level].push(node);

            node.children.forEach(childId => {
                calcLevel(nodeIdsToNodesMapTmp[childId], level + 1);
            });
        };

        const roots = learningPathData.nodes.filter((node: any) => node.parents.length === 0);
        if (!roots) return;
        roots.map((root: LearningPathNodeProps) => calcLevel(root, 0));

        setLevelToNodeIdsMap(levelToNodeIdsTmpMap);
    }, [learningPathData.nodes]);

    useEffect(() => {
        if (!areNodesRendered && containerRef?.current?.clientHeight > 0) {
            setAreNodesRendered(true);
        }
    }, [containerRef?.current?.clientHeight, areNodesRendered]);

    useEffect(() => {
        if (currentLevelRef.current && areNodesRendered && currentLevel !== -1 && currentLevel !== 0) {
            const containerClientRect = containerRef.current.getBoundingClientRect();
            const currLevelClientRect = currentLevelRef.current.getBoundingClientRect();
            const diff = currLevelClientRect.bottom - containerClientRect.bottom;
            const containerHeight = containerClientRect.bottom - containerClientRect.top;
            const levelHeight = currLevelClientRect.bottom - currLevelClientRect.top;
            const centerScroll = diff + containerHeight / 2 - levelHeight / 2;
            if (centerScroll < 0) {
                containerRef.current.scroll({ top: centerScroll, behavior: 'smooth' });
            }
        }
    }, [areNodesRendered, currentLevelRef, currentLevel]);

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (inventoryId !== '' && inventoryRef.current && !inventoryRef.current.contains(e.target)) {
                setInventoryId('');
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [inventoryId]);

    const handleMouseEnter = (id: string, type: string) => {
        setHoveredId(id);
        setHoveredType(type);
    };

    const handleMouseLeave = () => {
        setHoveredId(null);
        setHoveredType(null);
    };

    const getHoveredElementsForNode = useCallback(
        (node: LearningPathNodeProps, hoveredId: string): { type: string; id: string }[] => {
            const result = [{ type: 'node', id: hoveredId }];

            if (node && node.status === coreService.ProgressStatus.COMPLETED) {
                node.children.forEach(childId => {
                    const childNode = nodeIdsToNodesMap[childId];
                    if (childNode.status === null) {
                        result.push({ type: 'arrow', id: `arrow${node.id}-${childId}` });
                        result.push({ type: 'node', id: `node${childId}` });
                    }
                });
            } else if (node?.status === null) {
                node.parents.forEach(parentId => {
                    const parentNode = nodeIdsToNodesMap[parentId];
                    if (parentNode.status === coreService.ProgressStatus.COMPLETED) {
                        result.push({ type: 'arrow', id: `arrow${parentId}-${node.id}` });
                    }
                });
            }

            return result;
        },
        [nodeIdsToNodesMap]
    );

    const getHoveredElementsForArrow = useCallback(
        (arrowId: string): { type: string; id: string }[] => {
            const [nodeId1, nodeId2] = arrowId.replace('arrow', '').split('-');
            const node1 = nodeIdsToNodesMap[parseInt(nodeId1, 10)];
            const node2 = nodeIdsToNodesMap[parseInt(nodeId2, 10)];
            if (!node1 || !node2) return [];

            if (node1.status === coreService.ProgressStatus.COMPLETED && node2.status === null) {
                const result = [
                    { type: 'arrow', id: arrowId },
                    { type: 'node', id: `node${nodeId2}` }
                ];
                return result;
            }

            return [];
        },
        [nodeIdsToNodesMap]
    );

    const getHoveredElements = useCallback((): { type: string; id: string }[] => {
        if (!hoveredId || !hoveredType) return [];

        if (hoveredType === 'node') {
            const node = nodeIdsToNodesMap[parseInt(hoveredId.replace('node', ''), 10)];
            return getHoveredElementsForNode(node, hoveredId);
        }

        if (hoveredType === 'arrow') {
            return getHoveredElementsForArrow(hoveredId);
        }

        return [];
    }, [hoveredId, hoveredType, nodeIdsToNodesMap, getHoveredElementsForArrow, getHoveredElementsForNode]);

    useEffect(() => {
        setHoveredElements(getHoveredElements());
    }, [hoveredId, hoveredType, getHoveredElements]);

    const handleScroll = (event: any) => {
        const maxScroll = containerRef.current.scrollHeight - containerRef.current.clientHeight;
        if (maxScroll === 0) {
            setScrollPercent(0);
            return;
        }

        setScrollPercent(-(event.currentTarget.scrollTop / maxScroll) * 100);
    };

    const calcNodeStatus = (node: LearningPathNodeProps): ContentItemStatus => {
        if (node.status !== null) {
            return node.status;
        }
        let status: ContentItemStatus = 'notTakable';
        node.parents.forEach(parentId => {
            const parentNode = nodeIdsToNodesMap[parentId];
            if (parentNode.status === coreService.ProgressStatus.COMPLETED) {
                status = 'takable';
            }
        });
        return status;
    };

    const centerCoordinations = { x: 24.5, y: -1 };
    const leftCoordinations = [
        { x: 16, y: 0 },
        { x: 8.5, y: 2.8 },
        { x: 2.5, y: 7 }
    ];

    const rightCoordinations = [
        { x: 32.5, y: 0.4 },
        { x: 39.7, y: 3.5 },
        { x: 46.5, y: 8.2 }
    ];

    useEffect(() => {
        if (activeLearningPathUuid !== learningPaths[0].uuid) return;
        const newElements: any = [];
        const firstElement = learningPaths[0];
        newElements.push({ ...firstElement, top: centerCoordinations.y, left: centerCoordinations.x });
        for (let index = 1; index < learningPaths.length; index++) {
            const element = learningPaths[index];
            newElements.push({
                ...element,
                top: rightCoordinations[index - 1].y,
                left: rightCoordinations[index - 1].x
            });
        }

        setLearningPathWithLocations(newElements);
    }, [learningPaths]);

    useEffect(() => {
        selectedLearningPathUuid &&
            selectedLearningPathUuid !== null &&
            setActiveLearningPathUuid(selectedLearningPathUuid);
    }, [selectedLearningPathUuid]);

    useEffect(() => {
        if (displayedLearningPathUuid !== learningPathData.uuid) {
            setAreNodesRendered(false);
            setDisplayedLearningPathUuid(learningPathData.uuid);
        }
    }, [learningPathData]);

    useEffect(() => {
        setAreNavigationButtonRendered(false);
        onSelectLearningPath(activeLearningPathUuid);
        const newElements: any = [];
        const centeredElementIndex = learningPaths.findIndex(x => x.uuid === activeLearningPathUuid);

        for (let index = 0; index < learningPaths.length; index++) {
            let coordinations;
            if (index === centeredElementIndex) {
                coordinations = centerCoordinations;
            } else if (index < centeredElementIndex) {
                coordinations = leftCoordinations[centeredElementIndex - index - 1];
            } else if (index > centeredElementIndex) {
                coordinations = rightCoordinations[index - centeredElementIndex - 1];
            }

            const element = learningPaths[index];
            newElements.push({
                ...element,
                top: coordinations?.y,
                left: coordinations?.x
            });
        }

        setLearningPathWithLocations(newElements);
        setTimeout(() => {
            setAreNavigationButtonRendered(true);
        }, 500);
    }, [activeLearningPathUuid]);

    return (
        <div className={styles['skill-tree-navigation']}>
            <img
                src={`${process.env.PUBLIC_URL}/images/skillTreeGlow.svg`}
                className={styles['skill-tree-glow']}
                alt="skillTreeGlow"
            />
            <div className={styles['skill-tree-overview-wrapper']}>
                <SkillTreeOverview
                    title={translate(name)}
                    score={overallData.score}
                    progress={overallData.progress}
                    duration={overallData.duration}
                    languages={overallData.languages.map((lang: any) => languagesNameToDisplayName[lang])}
                    isSkillTree={true}
                    onEditLanguages={() => setShowProgramLanguageSelection(true)}
                    isDiplomaEnabled={props.status === 'completed'}
                    onShowCertificate={() => setShowSkillTreeCertificate(true)}
                />
            </div>
            <div className={styles['skill-tree-map-legend-wrapper']}>
                <MapLegend />
            </div>
            <div className={styles['learning-path-overview-wrapper']}>
                {viewedLearningPathUuid && (
                    <LearningPathOverviewContainer
                        learningPathUuid={viewedLearningPathUuid}
                        onClose={() => setViewedLearningPathUuid(null)}
                        key={viewedLearningPathUuid}
                    />
                )}
            </div>
            {showProgramLanguageSelection && (
                <div className={styles['program-langues-wrapper']}>
                    <ProgramLanguageSelectionContainer onClose={() => setShowProgramLanguageSelection(false)} />
                </div>
            )}

            <div ref={containerRef} className={styles['learning-path-container']} onScroll={handleScroll}>
                {Object.keys(levelToNodeIdsMap).map(level => {
                    const nodes = levelToNodeIdsMap[parseInt(level, 10)];
                    const isCurrentLevel = parseInt(level, 10) === currentLevel;

                    return (
                        <div
                            key={level}
                            style={{ display: 'flex', flexDirection: 'row' }}
                            ref={isCurrentLevel ? currentLevelRef : null}
                        >
                            {areNavigationButtonRendered &&
                                bottomRendered &&
                                nodes.map(node => {
                                    const isHovered = hoveredElements.some(element => element.id === `node${node.id}`);

                                    return (
                                        <div
                                            key={node.id}
                                            id={`node${node.id}`}
                                            className={styles['learning-path-node']}
                                            onMouseEnter={() => handleMouseEnter(`node${node.id}`, 'node')}
                                            onMouseLeave={handleMouseLeave}
                                            style={{
                                                position: 'relative',
                                                marginBottom: node.parents.length === 0 ? '25rem' : undefined
                                            }}
                                        >
                                            <ContentItem
                                                progress={node.progressPercentage}
                                                status={calcNodeStatus(node)}
                                                type={node.type}
                                                userNickname={props.nickname}
                                                userAvatar={props.profilePicture}
                                                duration={node.durationMinutes}
                                                name={node.name}
                                                points={node.status === null ? node.potentialScore : node.score}
                                                isHovered={isHovered}
                                                outerLineTo="left"
                                                onSelectNode={() => props.onSelectNode(node)}
                                                numberOfItems={node.numberOfItems}
                                                id={`node${node.id}`}
                                                setInventoryId={setInventoryId}
                                                isOptional={!node.isMandatory}
                                                isRoot={node.isRoot}
                                                showUserAvatar={
                                                    !showUserAvatarInRoot &&
                                                    learningPathData.lastSelectedNodeId === node.id
                                                }
                                            />
                                            {inventoryId === `node${node.id}` && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        zIndex: 999,
                                                        left: '6rem',
                                                        top: '2rem'
                                                    }}
                                                    ref={inventoryRef}
                                                >
                                                    <Inventory
                                                        name={node.name}
                                                        inventoryItems={node.itemTypesCount}
                                                        maxScore={node.potentialScore}
                                                        score={node.score}
                                                        onClose={() => setInventoryId('')}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    );
                })}
                {areNodesRendered &&
                    areNavigationButtonRendered &&
                    bottomRendered &&
                    learningPathData.nodes.map((node: any) => {
                        return node.children.map((childId: any) => {
                            const childNode = nodeIdsToNodesMap[childId];
                            const isActive =
                                node.status === coreService.ProgressStatus.COMPLETED && childNode?.status !== null;
                            const isHovered = hoveredElements.some(
                                element => element.id === `arrow${node.id}-${childId}`
                            );
                            return (
                                <div
                                    id={`arrow${node.id}-${childId}`}
                                    key={`${node.id}-${childId}-wrapper`}
                                    className={classNames({
                                        [styles['active-arrow']]: isActive,
                                        [styles['hovered-arrow']]: isHovered
                                    })}
                                    onMouseEnter={() => handleMouseEnter(`arrow${node.id}-${childId}`, 'arrow')}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <Xarrow
                                        startAnchor="top"
                                        endAnchor="bottom"
                                        key={`${node.id}-${childId}`}
                                        start={`node${node.id}`}
                                        end={`node${childId}`}
                                        strokeWidth={1}
                                        color={'#192F6E'}
                                    />
                                </div>
                            );
                        });
                    })}
                <div className={styles['skill-tree-navigation-bottom-wrapper']}>
                    <div className={styles['skill-tree-navigation-bottom']}>
                        <img
                            alt="skillTreeBottomBackground"
                            src={`${process.env.PUBLIC_URL}/images/skillTreeBottom.svg`}
                            className={styles.img}
                            onLoad={() => setBottomRendered(true)}
                        />
                        <div className={styles.name}>{name}</div>
                        {learningPathWithLocations &&
                            bottomRendered &&
                            learningPathWithLocations.map((learningPath: any) => (
                                <div
                                    key={learningPath.uuid}
                                    className={styles['skill-tree-navigation-item']}
                                    style={
                                        {
                                            '--top': `${learningPath.top}rem`,
                                            '--left': `${learningPath.left}rem`
                                        } as any
                                    }
                                    id={`node-root-${learningPath.uuid}`}
                                >
                                    <SkillTreePathButton
                                        name={learningPath.name}
                                        numberOfItems={learningPath.numberOfItems}
                                        isActive={activeLearningPathUuid === learningPath.uuid}
                                        onClick={() => {
                                            setActiveLearningPathUuid(learningPath.uuid);
                                            navigate(
                                                `/app/skillTree/?uuid=${uuid}&selectedLearningPathUuid=${learningPath.uuid}`,
                                                { replace: true }
                                            );
                                        }}
                                        onInformationClick={() => setViewedLearningPathUuid(learningPath.uuid)}
                                    />
                                    {showUserAvatarInRoot &&
                                        areNodesRendered &&
                                        learningPath.uuid === selectedLearningPathUuid && (
                                            <div className={styles['user-avatar-root']}>
                                                <Avatar name={props.nickname} avatarSrc={props.profilePicture} />
                                            </div>
                                        )}
                                </div>
                            ))}
                    </div>
                </div>
                {areNodesRendered &&
                    areNavigationButtonRendered &&
                    bottomRendered &&
                    levelToNodeIdsMap[0].map((node: any) => {
                        const isHovered = hoveredElements.some(element => element.id === `arrow${node.id}-root`);
                        return (
                            <div
                                id={`arrow${node.id}-root`}
                                key={`${node.id}-root-wrapper`}
                                className={classNames({
                                    [styles['active-arrow']]: true,
                                    [styles['hovered-arrow']]: isHovered
                                })}
                                onMouseEnter={() => handleMouseEnter(`arrow${node.id}-root`, 'arrow')}
                                onMouseLeave={handleMouseLeave}
                            >
                                <Xarrow
                                    startAnchor="top"
                                    endAnchor="bottom"
                                    key={`${node.id}-${learningPathData.uuid}`}
                                    start={`node-root-${learningPathData.uuid}`}
                                    end={`node${node.id}`}
                                    strokeWidth={1}
                                    color={'#192F6E'}
                                />
                            </div>
                        );
                    })}

                <div
                    className={styles['certificate-wrapper']}
                    style={{ backgroundImage: bottomRendered && areNavigationButtonRendered ? '' : 'none' }}
                >
                    <img
                        alt="skillTreeStatus"
                        className={
                            styles[
                                props.learningPathData.status === 'completed'
                                    ? 'certificate-img'
                                    : 'certificate-img-disabled'
                            ]
                        }
                        src={`${process.env.PUBLIC_URL}/images/learningPathCertificateDetails.webp`}
                        onClick={() =>
                            props.learningPathData.status === 'completed' ? setShowLpCertificate(true) : undefined
                        }
                        style={{ display: bottomRendered && areNavigationButtonRendered ? 'block' : 'none' }}
                    />
                </div>
            </div>
            {showLpCertificate && (
                <CertificatePopupContainer
                    inputCertificateData={{
                        entityDisplayName: props.learningPathData.name,
                        name: props.nickname,
                        completedAt: props.learningPathData.completedAt || ''
                    }}
                    onClose={() => setShowLpCertificate(false)}
                    isCustom={false}
                    isSkillTree={false}
                    isLearningPath={true}
                />
            )}
            {showSkillTreeCertificate && (
                <CertificatePopupContainer
                    inputCertificateData={{
                        name: fullName || nickname,
                        entityDisplayName: props.name,
                        completedAt: props.completedAt || ''
                    }}
                    onClose={() => setShowSkillTreeCertificate(false)}
                    isCustom={false}
                    isSkillTree={true}
                    isLearningPath={false}
                />
            )}
        </div>
    );
};

export interface SkillTreeNavigationProps {
    name: string;
    uuid: string;
    learningPaths: { name: string; numberOfItems: string; uuid: string }[];
    overallData: any;
    completedAt: string;
    onSelectLearningPath: Function;
    onSelectNode: Function;
    status: string;
    selectedLearningPathUuid?: string | null;
    learningPathData: any;
    nickname: string;
    profilePicture?: string;
    tenant: string;
}

export default SkillTreeNavigation;
